// Model stránky „Servis nářadí" (`/servis-naradi/`) — jedna definice parserů
// nad textem z administrace pro DVA konzumenty: samotnou stránku
// (servicePage.js, hostí ji `article.pageArticleDetail [itemprop=about]`)
// a modal „Záruční a pozáruční servis" na detailu produktu (serviceModal.js),
// který si tutéž stránku stáhne fetchem a ukáže z ní destilát.
//
// Nativní stránka je `type-page` s prostým textem z administrace:
//   p (leadová věta + perex) · ul (servis všech značek) · p (Specializujeme se…)
//   h2 „Co nabízíme…" + ul (7 položek, ceny v textu)
//   h3 „Ceník…" + ul (3 položky „Název: 1 200 Kč (poznámka)")
//   h2 „Proč si vybrat…" + ul (4 položky „<b>Titulek:</b> text")
// Všechno se z toho STAVÍ — ceny, značky i popisy čteme z reálného obsahu,
// ne z konstant. Když klient text v adminu změní, změní se stránka i modal;
// když sekce zmizí, blok se vypustí.
//
// Fetch pro modal jede BEZ COOKIES (`credentials: 'omit'`, stejný vzor jako
// shippingModel.js): stránka je pro všechny stejná a bez session ji CDN
// i prohlížeč cachují. Cache promisu žije po celý pageview.

import { textOf } from './infoPage.js';
import { page, pageSlug } from './pages.js';
import L from '../locale/cs/serviceModel.js';

// slug a href stránky servisu = `pages.service` z manifestu shopu
export const SERVICE_SLUG = pageSlug('service');
export const SERVICE_HREF = page('service');

// Texty → locale vrstva (S3 #399); dluh per soubor měří tools/lib/locale-debt.mjs — sdílené stránkou i modalem
export const LABEL_EYEBROW = L.eyebrow;
export const LABEL_SPEC = L.spec;
// Ceny servisu jsou orientační („od" — zadání Tomáše 2026-09-09), jen diagnostika
// má pevnou cenu; konečnou částku řekne servis po diagnostice
export const LABEL_PRICE_PEREX = L.pricePerex;
export const LABEL_BAND_TITLE = L.bandTitle;
export const LABEL_BAND_TEXT = L.bandText;

// Ikony podle klíčových slov v názvu položky (pořadí rozhoduje)
export const SERVICE_ICONS = L.serviceIcons;
export const PRICE_ICONS = L.priceIcons;
export const WHY_ICONS = L.whyIcons;

export function pickIcon(table, text, fallback) {
  for (let i = 0; i < table.length; i += 1) {
    if (table[i][0].test(text)) return table[i][1];
  }
  return fallback;
}

// Odkaz na stránku servisu bereme přednostně z existující navigace (patička,
// menu), ať se slug nerozejde s administrací; konstanta je jen fallback.
export function serviceHref() {
  const link = document.querySelector('a[href*="' + SERVICE_SLUG + '"]');
  return link ? link.getAttribute('href') : SERVICE_HREF;
}

// ---------------------------------------------------------------------------
// Parsování obsahu z administrace
// ---------------------------------------------------------------------------
function listAfter(heading) {
  let node = heading ? heading.nextElementSibling : null;
  while (node && node.tagName !== 'UL' && node.tagName !== 'OL') {
    if (/^H[1-4]$/.test(node.tagName)) return null;
    node = node.nextElementSibling;
  }
  return node && (node.tagName === 'UL' || node.tagName === 'OL') ? node : null;
}

function headingBy(host, re) {
  const heads = host.querySelectorAll('h2, h3, h4');
  for (let i = 0; i < heads.length; i += 1) {
    if (re.test(textOf(heads[i]))) return heads[i];
  }
  return null;
}

// „… za cenu 199 Kč" / „… od 70 Kč" / „… cca 100 Kč" / „… - ZDARMA" → cenový
// chip + očištěný text; „od"/„cca" zůstává v chipu (cena je orientační)
function splitPrice(text) {
  let rest = text;
  let chip = null;

  // `\b` jako v parsePricing: bez něj text ‚elektrod 100 Kč' dal chip ‚od 100 Kč'
  // a název ‚Výměna elektr' (code review 2026-09-09)
  const paid = L.priceWordRe.exec(rest);
  if (paid) {
    chip = pricePrefix(paid[1]) + paid[2].replace(/\s+/g, ' ');
    rest = rest.slice(0, paid.index);
  } else {
    const free = L.freeSuffixRe.exec(rest);
    if (free) {
      chip = L.freeLabel;
      rest = rest.slice(0, free.index);
    }
  }
  return { text: rest.replace(/[\s.]+$/, ''), chip: chip };
}

// „od" / „cca" před částkou se nese dál („od 1 200 Kč"), „za cenu" ne
function pricePrefix(word) {
  const w = String(word || '').toLowerCase().replace(/\s+/g, ' ');
  if (w === L.wordFrom || w === L.wordAlreadyFrom) return L.prefixFrom;
  if (w === L.wordCca) return L.prefixCca;
  return '';
}

function splitTitle(text) {
  const m = /\s+[-–—]\s+/.exec(text);
  if (!m) return { title: text, desc: null };
  return { title: text.slice(0, m.index).trim(), desc: text.slice(m.index + m[0].length).trim() };
}

export function parseServices(host) {
  const heading = headingBy(host, L.headingServicesRe);
  const list = listAfter(heading);
  if (!list) return { heading: heading, items: [] };
  const items = [];
  list.querySelectorAll(':scope > li').forEach(function (li) {
    const raw = textOf(li);
    if (!raw) return;
    const priced = splitPrice(raw);
    const split = splitTitle(priced.text);
    const anchor = li.querySelector('a[href]');
    items.push({
      title: split.title,
      desc: split.desc,
      chip: priced.chip,
      href: anchor ? anchor.getAttribute('href') : null,
      icon: pickIcon(SERVICE_ICONS, split.title, 'wrench'),
    });
  });
  return { heading: heading, items: items };
}

export function parsePricing(host) {
  const heading = headingBy(host, L.headingPricingRe);
  const list = listAfter(heading);
  if (!list) return { heading: heading, items: [] };
  const items = [];
  list.querySelectorAll(':scope > li').forEach(function (li) {
    const raw = textOf(li);
    const parts = /^(.+?)\s*:\s*(.+)$/.exec(raw);
    if (!parts) return;
    const name = parts[1].trim();
    const price = L.pricingValueRe.exec(parts[2]);
    const note = /\(([^)]+)\)/.exec(parts[2]);
    if (!price) return;
    items.push({
      title: name,
      // rozpad pro render s malým „od" (ceny jsou orientační, 2026-09-09) —
      // drží se z parsování, ne zpětným regexem nad složeným řetězcem
      priceFrom: pricePrefix(price[1]).trim(),
      priceAmount: price[2].replace(/\s+/g, ' '),
      note: note ? note[1].charAt(0).toUpperCase() + note[1].slice(1) : null,
      icon: pickIcon(PRICE_ICONS, name, 'cog'),
    });
  });
  return { heading: heading, items: items };
}

export function parseWhy(host) {
  const heading = headingBy(host, L.headingWhyRe);
  const list = listAfter(heading);
  if (!list) return { heading: heading, items: [] };
  const items = [];
  list.querySelectorAll(':scope > li').forEach(function (li) {
    const strong = li.querySelector('b, strong');
    const raw = textOf(li);
    let title = strong ? textOf(strong) : raw;
    let desc = strong ? raw.slice(textOf(strong).length) : null;
    if (!strong) {
      const colon = /^(.+?)\s*:\s*(.+)$/.exec(raw);
      if (colon) {
        title = colon[1];
        desc = colon[2];
      }
    }
    title = title.replace(/\s*:\s*$/, '').trim();
    desc = desc ? desc.replace(/^\s*:\s*/, '').trim() : null;
    if (!title) return;
    items.push({ title: title, desc: desc, icon: pickIcon(WHY_ICONS, title, 'shield') });
  });
  return { heading: heading, items: items };
}

// „Specializujeme se … na námi prodávané značky: BASS, BOXER, …"
export function parseBrands(host) {
  const paras = host.querySelectorAll(':scope > p');
  for (let i = 0; i < paras.length; i += 1) {
    const text = textOf(paras[i]);
    if (!L.specializeRe.test(text)) continue;
    const after = text.split(':')[1];
    if (!after) continue;
    const names = after
      .replace(/\.\s*$/, '')
      .split(',')
      .map(function (n) {
        return n.trim();
      })
      .filter(Boolean);
    if (names.length) return { node: paras[i], names: names };
  }
  return { node: null, names: [] };
}

// První odstavec = leadová věta (tučně) + zbytek perexu
export function parseLead(host) {
  const first = host.querySelector(':scope > p');
  if (!first) return null;
  const strong = first.querySelector('b, strong');
  const whole = textOf(first);
  const lead = strong ? textOf(strong) : '';
  const rest = lead && whole.indexOf(lead) === 0 ? whole.slice(lead.length).trim() : whole;
  return { node: first, lead: lead, rest: rest };
}

// „Provádíme servis všech značek." → text do pásu značek
export function parseAllBrandsClaim(host) {
  const li = host.querySelector(':scope > ul li');
  if (!li) return { node: null, text: null };
  const strong = li.querySelector('b, strong');
  return {
    node: li.closest('ul'),
    text: (strong ? textOf(strong) : textOf(li)).replace(/\.\s*$/, ''),
  };
}

/**
 * Celý model stránky nad hostem `[itemprop=about]` — stránka i modal ho čtou
 * stejně. `quick` = položky servisu s cenovým chipem (diagnostika, rady zdarma).
 */
export function parseServiceContent(about) {
  const services = parseServices(about);
  return {
    lead: parseLead(about),
    claim: parseAllBrandsClaim(about),
    brands: parseBrands(about),
    services: services,
    pricing: parsePricing(about),
    why: parseWhy(about),
    quick: services.items.filter(function (item) {
      return !!item.chip;
    }),
  };
}

/** Model ze staženého HTML stránky; `null`, když stránka host nemá. */
export function parseServiceHtml(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const about = doc.querySelector('article.pageArticleDetail [itemprop="about"]');
  if (!about) return null;
  const model = parseServiceContent(about);
  const h1 = doc.querySelector('article.pageArticleDetail header h1');
  model.title = h1 ? textOf(h1) : '';
  // bez rozpoznaného obsahu není z čeho destilovat — volající ukáže odkaz
  if (!model.services.items.length && !model.pricing.items.length) return null;
  return model;
}

// --- sdílený fetch ----------------------------------------------------------
let cached = null;

export function loadServicePage(href) {
  if (!window.fetch || location.protocol === 'file:') return Promise.resolve(null);
  if (cached) return cached;
  cached = fetch(href || serviceHref(), { credentials: 'omit' })
    .then(function (r) {
      return r.ok ? r.text() : '';
    })
    .then(function (html) {
      return html ? parseServiceHtml(html) : null;
    })
    .catch(function () {
      return null;
    });
  return cached;
}
